import { action, computed, makeObservable, observable } from "mobx"

type Constructor<T = {}> = new (...args: any[]) => T
type AbstractConstructor<T = {}> = abstract new (...args: any[]) => T

class Empty {}

export function loading<
  T extends AbstractConstructor | Constructor = Constructor,
>(loaders: string[], target: T = Empty as T) {
  return class extends target {
    isReadonly = false
    loaders: Record<string, boolean> = {
      ...loaders.reduce(
        (acc, key) => ({ ...acc, [`${key}Status`]: false }),
        {},
      ),
    }

    constructor(...args: any[]) {
      super(...args)

      makeObservable(this, {
        isLoading: computed,
        isReadonly: observable,
        loaders: observable,
        resetLoadersState: action.bound,
        setIsReadonly: action.bound,
        setLoaderState: action.bound,
      })

      loaders.forEach((loader) => {
        const cb = this[loader as keyof typeof this] as Function

        const key = `${loader}Status`

        if (cb) {
          // @ts-ignore
          this[loader as keyof typeof this] = (...args) => {
            setTimeout(() => this.setLoaderState(key, true))

            return cb
              .bind(this)(...args)
              .finally(() => this.setLoaderState(key, false))
          }
        }
      })
    }

    resetLoadersState() {
      Object.keys(this.loaders).forEach((key) =>
        this.setLoaderState(key, false),
      )
    }

    setIsReadonly(isReadonly: boolean) {
      this.isReadonly = isReadonly
    }

    setLoaderState(name: string, state: boolean) {
      this.loaders[name] = state
    }

    get isLoading() {
      return Object.values(this.loaders).some(Boolean)
    }
  }
}
